<template>
    <form :id="id" @submit.prevent="handleSubmit" class="form-horizontal">
        <fieldset :disabled="disabled">
            <x-form-group label="Label"
                :error="getError('label')"
                :is-required="true"
                help-text="Label hanya akan tampil di backend dan tidak ditampilkan ke user.">
                <input type="text"
                    class="form-control"
                    v-model="fields.label"
                    required />
            </x-form-group>
            <x-form-group label="Nama Field"
                :error="getError('field_name')"
                :is-required="true"
                help-text="Nama field yang akan ditampilkan ke user, akan muncul di halaman registrasi.">
                <input type="text"
                    class="form-control"
                    v-model="fields.field_name"
                    required />
            </x-form-group>
            <x-form-group label="Tipe"
                :error="getError('type')"
                :is-required="true">
                <select class="form-control" v-model="fields.type">
                    <option v-for="(type, index) in availableTypes" :key="index" :value="type.key">
                        {{ type.label }}
                    </option>
                </select>
            </x-form-group>
            <x-form-group label="Options"
                :error="getError('options')"
                :is-required="true"
                v-if="shouldShowOptions"
                help-text="Ketik dan tekan 'Enter' untuk menampahkan option">
                <multiselect open-direction="bottom"
                    v-model="fields.options"
                    tag-placeholder="Add option"
                    placeholder="Create option"
                    :taggable="true"
                    :multiple="true"
                    :options="fields.options"
                    class="form-control"
                    @tag="addOption" />
            </x-form-group>
            <x-form-group label="Rules"
                :error="getError('rules')"
                v-show="fields.type">
                <x-table :headers="[
                    '',
                    'Operator',
                    'Tipe',
                    'Value',
                    '',
                ]" v-if="renderRules">
                    <template v-for="(rule, index) in fields.rules">
                        <validation-rule-form-item 
                            :key="index"
                            :index="index"
                            :show-operator="index > 0"
                            :available-options="fields.options"
                            :available-operators="availableOperators"
                            :available-comparators="availableComparators"
                            :allow-sub-rules="true"
                            :type="fields.type"
                            v-model="fields.rules[index]"
                            @remove="removeRule"
                            @addSubRule="addSubRule(index)" />
                        <tr :key="`sub-rules-${index}`" v-if="rule.rules.length > 0">
                            <td></td>
                            <td colspan="4">
                                <b>Sub-rules</b>
                                <x-table>
                                    <validation-rule-form-item v-for="(subRule, subIndex) in rule.rules"
                                        :key="`sub-rules-${index}-${subIndex}`"
                                        :index="subIndex"
                                        :available-options="fields.options"
                                        :available-operators="availableOperators"
                                        :available-comparators="availableComparators"
                                        :type="fields.type"
                                        v-model="rule.rules[subIndex]"
                                        @remove="sub => removeSubRule(sub, index)" />
                                </x-table>
                            </td>
                        </tr>
                    </template>
                    <template #footer="{ colspan }">
                        <tr>
                            <td :colspan="colspan" class="text-center">
                                <button type="button"
                                    @click.prevent="addRule"
                                    class="btn btn-default">
                                    <i class="fa fa-plus" /> Add
                                </button>
                            </td>
                        </tr>
                    </template>
                </x-table>
            </x-form-group>
            <x-form-group label="Error Message"
                :error="getError('error_message')"
                :is-required="true">
                <textarea type="text"
                    class="form-control"
                    v-model="fields.error_message"
                    required />
            </x-form-group>
        </fieldset>
    </form>
</template>

<script>
import XFormGroup from '../../components/XFormGroup.vue'
import XTable from '../../components/XTable.vue'
import FormErrorMixin from '../../mixins/FormErrorMixin'
import { comparators, operators, types } from './constants'
import ValidationRuleFormItem from './ValidationRuleFormItem.vue'

const availableComparators = [
    { key: comparators.contains, label: 'Mengandung' },
    { key: comparators.equals, label: '=' },
    { key: comparators.greaterThan, label: '>' },
    { key: comparators.lessThan, label: '<' },
]

const availableOperators = [
    { key: operators.and, label: 'Dan' },
    { key: operators.or, label: 'Atau' },
]

const availableTypes = [
    { key: types.age, label: 'Usia' },
    { key: types.checkbox, label: 'Checkbox' },
    { key: types.number, label: 'Angka' },
    { key: types.radio, label: 'Radio' },
    { key: types.text, label: 'Teks' },
]

export default {
    components: {
        ValidationRuleFormItem,
        XFormGroup,
        XTable,   
    },
    computed: {
        availableComparators() {
            return availableComparators.filter(comparator => {
                return !this.forbiddenComparators.includes(comparator.key)
            })
        },
        availableOperators() {
            return availableOperators
        },
        availableTypes() {
            return availableTypes
        },
        forbiddenComparators() {
            if (this.fields.type === types.age) {
                return [
                    comparators.contains
                ]
            }
            if (this.fields.type === types.checkbox) {
                return [
                    comparators.equals,
                    comparators.greaterThan,
                    comparators.lessThan,
                ]
            }
            if (this.fields.type === types.number) {
                return [
                    comparators.contains,
                ]
            }
            if (this.fields.type === types.radio) {
                return [
                    comparators.contains,
                    comparators.greaterThan,
                    comparators.lessThan,
                ]
            }
            if (this.fields.type === types.text) {
                return [
                    comparators.greaterThan,
                    comparators.lessThan,
                ]
            }
            return []
        },
        shouldShowOptions() {
            return this.fields.type === types.checkbox
                || this.fields.type === types.radio
        },
    },
    created() {
        this.fields = {
            ...this.fields,
            ...this.initialFields,
        }
    },
    data() {
        return {
            fields: {
                error_message: null,
                field_name: null,
                label: null,
                options: [],
                rules: [],
                type: null,
            },
            renderRules: true,
        }
    },
    methods: {
        addOption(option) {
            this.fields.options.push(option)
        },
        addRule() {
            this.fields.rules.push(this.getDefaultRule())
        },
        addSubRule(index) {
            this.fields.rules[index].rules.push(this.getDefaultRule())
        },
        getDefaultRule() {
            return {
                operator: _.get(this.availableOperators, '0.key', null),
                type: _.get(this.availableComparators, '0.key', null),
                value: null,
                rules: [],
            }
        },
        handleSubmit() {
            this.$emit('submit', this.fields)
        },
        removeRule(targetIndex) {
            this.fields.rules = this.fields.rules.filter((rule, index) => {
                return index != targetIndex
            })
            this.rerenderRules()
        },
        removeSubRule(targetIndex, parentIndex) {
            this.fields.rules[parentIndex].rules = this.fields.rules[parentIndex].rules.filter((rule, index) => {
                return index != targetIndex
            })
            this.rerenderRules()
        },
        rerenderRules() {
            this.renderRules = false
            setTimeout(() => {
                this.renderRules = true
            }, 5)
        },
    },
    mixins: [
        FormErrorMixin,
    ],
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        errors: {
            type: Object,
            required: false,
            default: null,
        },
        id: {
            type: String,
            required: false,
            default: null,
        },
        initialFields: {
            type: Object,
            required: false,
            default: () => ({}),
        },
    },
    watch: {
        'fields.type': {
            handler(val, prev) {
                if (!Boolean(prev)) {
                    return
                }
                if (val == prev) {
                    return
                }
                this.fields.rules = []
            },
            deep: true,
        }
    },
}
</script>